import {useEffect, useMemo, useRef} from 'react';
import {useSyncExternalStore} from 'use-sync-external-store/shim';
import {Conversation} from '@stripe-internal/conversations-client';
import {ConversationPoller, PollFn} from '../lib/ConversationPoller';
import {useClient} from '../ClientProvider';

function isDynamic(value: unknown): boolean {
  return value === 'if_latest' || value === 'active';
}

// Fallback for messages missing data.conversationsApi
// Framework IDs follow "{serverMsgId}.{partId}".
function serverMessageIdFromFrameworkId(
  frameworkId: string,
): string | undefined {
  const lastDot = frameworkId.lastIndexOf('.');
  if (lastDot <= 0) {
    return undefined;
  }
  return frameworkId.slice(0, lastDot);
}

function getDynamicMessageIds(conversation: Conversation): string[] {
  if (!conversation.messages) {
    return [];
  }
  const ids = new Set<string>();
  for (const message of Object.values(conversation.messages)) {
    const data = message.data as
      | {dynamic?: string; conversationsApi?: {messageId: string}}
      | undefined;
    const partDynamic =
      message.type === 'custom-part-conversation-message' && 'part' in message
        ? (message as any).part?.dynamic
        : undefined;
    if (isDynamic(partDynamic) || isDynamic(data?.dynamic)) {
      const serverMsgId =
        data?.conversationsApi?.messageId ??
        serverMessageIdFromFrameworkId(message.id);
      if (serverMsgId) {
        ids.add(serverMsgId);
      }
    }
  }
  return Array.from(ids);
}

export function useConversationPoller(conversation: Conversation): {
  isComplete: boolean;
  error: string | null;
} {
  const client = useClient();
  const pollRef = useRef<PollFn>(async () => ({
    type: 'success' as const,
    messages: {},
  }));
  pollRef.current = async () => {
    if (
      !('pollConversation' in client) ||
      typeof client.pollConversation !== 'function'
    ) {
      return {type: 'success' as const, messages: {}};
    }

    const dynamicMessageIds = getDynamicMessageIds(conversation);

    return client.pollConversation({
      conversationId: conversation.id,
      participantsById: conversation.participants,
      user: conversation.user,
      dynamicMessageIds,
    });
  };

  const poller = useMemo(
    () => new ConversationPoller(() => pollRef.current(), conversation),
    [conversation],
  );

  useEffect(() => () => poller.destroy(), [poller]);

  const state = useSyncExternalStore(
    (callback) => poller.subscribe(callback),
    () => poller.getState(),
  );

  useEffect(() => {
    poller.startPolling();
    return () => poller.stopPolling();
  }, [poller]);

  return state;
}
